import { BehaviorSubject, Subscription, throwError as observableThrowError } from "rxjs"
import { HttpClient } from "@angular/common/http"
import { EventEmitter, Injectable } from "@angular/core"
import { catchError, map } from "rxjs/operators"
import { Router } from "@angular/router"
import { NgbModal } from "@ng-bootstrap/ng-bootstrap"
import { Media, TranscriptMedia } from "../models/resource/media"
import { Resource } from "../models/resource/resource"
import { StandardsInformation } from "../models/resource/standards"
import { TextDisplayModalComponent } from "../shared-components/modals/text-display-modal/text-display-modal.component"
import { LmApiService } from "./lm-api.service"
import { LocalizationService } from "./localization.service"
import { URSService } from "./urs.service"

@Injectable({
    providedIn: "root",
})
export class ResourceService {
    public initialData = new Resource()
    public initialStandardsInfo = new StandardsInformation()
    public resourceResponse = new BehaviorSubject<Resource>(this.initialData)
    public standardsResponse = new BehaviorSubject<StandardsInformation>(this.initialStandardsInfo)
    private subscriptions: Subscription[] = []

    constructor(
        private http: HttpClient,
        private lmApi: LmApiService,
        private localizationService: LocalizationService,
        private router: Router,
        private ursService: URSService,
        private modalService: NgbModal
    ) {}

    public resetData() {
        // Reset, so we don't display obsolete data
        this.resourceResponse.next(this.initialData)
        this.standardsResponse.next(this.initialStandardsInfo)
    }

    public getResource(resourceCode) {
        this.resetData()
        this.cancelSubscriptions()
        const resourceURL = `/api/v2/resource/${resourceCode}/`
        this.subscriptions.push(
            this.lmApi
                .getWithCookiesInParams(resourceURL, ["user_content_permission", "student"])
                .pipe(
                    catchError((error) => observableThrowError(error)),
                    map((response: any) => Resource.fromData(response))
                )
                .subscribe((resource: Resource) => {
                    // todo: if invalid, raise 404
                    this.resourceResponse.next(resource)
                    this.updateUrsRestrictions(resource)
                    if (resource.hasStandards) {
                        this.setStandardsInformation(resource.resourceCode)
                    }
                })
        )
    }

    public updateUrsRestrictions(resource: Resource) {
        resource.assets.forEach((asset) =>
            asset.medias.forEach((media) => media.evaluateURSRestriction(this.ursService))
        )
    }

    public incrementViewCount(resourceCode: string) {
        this.lmApi
            .get(`/resource/view-count/${resourceCode}/`)
            .pipe(catchError((error) => observableThrowError(error)))
            .subscribe()
    }

    public getLessonPlanContent(resourceCode) {
        // This method will be used from lesson plan layout component
        return this.lmApi
            .get(`/api/v2/lesson_plan_html/${resourceCode}`)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public getOrCreateActivity(resourceCode) {
        return this.lmApi
            .get(`/api/v2/activity_for_resource/${resourceCode}/`)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public getLocalStorageShareAssignModalKey() {
        return "shouldOpenShareAssignModal"
    }

    public getGoogleClassroomSharableLink(resourceCode: string, isLoggedIn: boolean) {
        const resourceUrlToShareGoogleClass =
            "https://classroom.google.com/share?url=" + window.location
        const googleClassWindow = window.open(
            "",
            "_blank",
            "width=640, height=450,toolbar=no, scrollbars=yes"
        )
        if (isLoggedIn) {
            // TODO: DON'T ADD this subscription to subscriptions to be deleted, since it does a post on activity
            // endpoint and we don't want to leave the it in an inconsistent state
            this.getOrCreateActivity(resourceCode).subscribe((activityData: any) => {
                if (activityData.isAssignable) {
                    googleClassWindow.location.href =
                        "https://classroom.google.com/share?url=" +
                        activityData.activity.student_url +
                        "&title=" +
                        activityData.activity.name
                } else {
                    googleClassWindow.location.href = resourceUrlToShareGoogleClass
                }
            })
        } else {
            googleClassWindow.location.href = resourceUrlToShareGoogleClass
        }
    }

    public getAssetDownloadUrl(media: Media) {
        return this.lmApi
            .get(media.downloadGenerateURL)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    public getLessonPlanExportUrl(resource: Resource) {
        return this.lmApi
            .get(`/api/v2/lesson_plan_word/${resource.resourceCode}`)
            .pipe(catchError((error) => observableThrowError(error)))
    }

    private cancelSubscriptions() {
        this.subscriptions.map((subscription) => subscription.unsubscribe())
    }

    private getResourceStandards(resourceCode) {
        let standardsURL = `/api/v2/lo/standards/r/${resourceCode}/`
        if (this.localizationService.hasOrganization()) {
            standardsURL += `${this.localizationService.getOrganizationId()}/`
        }
        return this.lmApi
            .getWithCookiesInParams(standardsURL, ["logged_in"])
            .pipe(catchError((error) => observableThrowError(error)))
    }

    private setStandardsInformation(resourceCode) {
        this.subscriptions.push(
            this.getResourceStandards(resourceCode).subscribe((resStandards: any) => {
                if (this.resourceResponse.getValue().resourceCode != resourceCode) {
                    return
                }
                const standardsInfo = StandardsInformation.fromData(resStandards, resourceCode)
                this.standardsResponse.next(standardsInfo)
            })
        )
    }

    public downloadTranscript(media: TranscriptMedia) {
        if (media.transcriptURL) {
            const win = window.open(media.transcriptURL, "_blank")
            win.focus()
        } else {
            const modalRef = this.modalService.open(TextDisplayModalComponent, {
                size: "lg",
                windowClass: "text-display-modal",
            })
            modalRef.componentInstance.text = media.transcriptText
            modalRef.componentInstance.printURL = media.transcriptPrintURL
        }
    }
}
